import type { Recipe } from '../../lib/supabase'
import type { Cover as CoverModel } from '../../lib/recipeBook'
import { ingredientCount } from '../../lib/recipeBook'
import { CheckIcon, StarIcon } from '../icons'
import { Cover } from './Cover'

export interface RecipeCardProps {
  recipe: Recipe
  cover: CoverModel
  /** Its photo's signed URL, when it has one. */
  photoUrl?: string
  /** "koket.se" or "Added by Anna" (recipeSource). */
  source: string
  /** Ingredients neither on the list nor in the basket. */
  missing: number
  selected: boolean
  isNew: boolean
  tintFor: (categoryId: string | null) => string
  onToggleSelect: () => void
  onToggleStar: () => void
  onOpen: () => void
}

// One recipe in the grid (feature spec 02 "Recipe card"), a tile two to a row
// on the phone, four on desktop: a square cover with the time, the selector
// (tick to shop for it), the star and New; under it the name and one line of
// facts — the servings and the one thing worth knowing: what's missing from
// the list, else the page's rating, else where it came from, else how many
// ingredients. Tags live in the filters.
export function RecipeCard({ recipe: r, cover, photoUrl, source, missing, selected, isNew, tintFor, onToggleSelect, onToggleStar, onOpen }: RecipeCardProps) {
  const count = ingredientCount(r)
  const fact = missing > 0
    ? <span className="ml-recipe-card__missing">Missing {missing}</span>
    : r.rating_value != null
      ? <span className="ml-recipe-card__rating" aria-label={`Rated ${r.rating_value} of 5`}><StarIcon size={11} filled />{r.rating_value.toFixed(1)}</span>
      : source ? <span>{source}</span> : <span>{count} {count === 1 ? 'ingredient' : 'ingredients'}</span>
  return (
    <li className="ml-recipe-cell">
      <article className={`ml-recipe-card${selected ? ' is-selected' : ''}`} aria-label={r.title}>
        <div className="ml-recipe-card__cover">
          <Cover cover={cover} tintFor={tintFor} minutes={r.minutes} photoUrl={photoUrl} size="square" />
          <button type="button" role="checkbox" aria-checked={selected} className={`ml-select${selected ? ' is-on' : ''}`}
            aria-label={`Select ${r.title} for shopping`} onClick={onToggleSelect}>
            <span className="ml-select__dot">{selected && <CheckIcon size={14} />}</span>
          </button>
          <button type="button" className="ml-star" aria-pressed={r.starred} aria-label={`Star ${r.title}`} onClick={onToggleStar}>
            <StarIcon size={15} filled={r.starred} />
          </button>
          {isNew && <span className="ml-new-badge">New</span>}
        </div>
        <button type="button" className="ml-recipe-card__body" aria-label={`Open ${r.title}`} onClick={onOpen}>
          <h3 className="ml-recipe-card__name ml-clamp-2">{r.title}</h3>
          <span className="ml-recipe-card__meta">
            {r.servings != null && <span>{r.servings} servings</span>}
            {fact}
          </span>
        </button>
      </article>
    </li>
  )
}
